/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.USER.LIST — mobile-native.
 *
 * The touch-first counterpart of src/pages/users/UserListPage.tsx. It is a
 * DISTINCT presentation implementation (dense card list, facet chips, bottom-sheet
 * detail) that shares the desktop page's DATA/LOGIC layer only:
 *   - reads via apiDb (same csm_user_master / csm_role_master queries),
 *   - the activate/deactivate write routes through the Common Routine
 *     (routedUpdate) exactly like the desktop page — §2 single write path.
 * It never re-renders the desktop grid; §16.
 */
/* tl-group-ok: the listed row is a csm_user_master, which has no _tl — a person is not
   translated. The embedded csm_role_master_tl resolves the NAMES of a user's roles (§17.B4);
   those translations belong to the role, not to this row. Desktop UserListPage is waived for
   the same reason. */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Repeat, Power, X, SquarePen } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { fetchAllRows } from '../../lib/fetch_all_rows';
import { useActiveLocale } from '../../lib/use_active_locale';
import { localeName } from '../../i18n/locale_display';
import { routedUpdate } from '../../lib/routed_writes';
import { MobileRelatedChildren, type MobileChildGroup, type MobileChildItem } from '../components/MobileRelatedChildren';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { MobileUserEditSheet, type MobileEditableUser } from './MobileUserEditSheet';
import { hasAccess, isSuperAdmin } from '../../types/auth';
import { collectReportingSubtree } from '../../lib/reporting_line';
import { useCanDeactivateUser } from '../../lib/use_can_deactivate_user';
import { registerMobileChildEditor, type MobileChildEditorProps } from '../lib/mobile_child_editors';

const PAGE_CODE = 'CSM.USER.LIST';

interface MobileUserRow {
  user_id: number;
  email: string;
  display_name: string;
  is_admin: 'Y' | 'N';
  is_active: 'Y' | 'N';
  preferred_locale: string;
  manager_user_id: number | null;
  role_codes: string[];
}

type StatusFacet = 'all' | 'active' | 'inactive';

interface TlRow { locale_code: string; display_text: string }

function initialsOf(row: MobileUserRow): string {
  const base = (row.display_name || row.email || '?').trim();
  const parts = base.split(/[\s@.]+/).filter(Boolean);
  const chars = parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2);
  return chars.toUpperCase();
}

export function MobileUserListPage({ session, onBeginMimic }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  // §17.B4: resolve preferred_locale (EN_US) to its display name, from the one
  // function that names a language (lib/language_options_core).
  const [users, setUsers] = useState<MobileUserRow[]>([]);
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileUserRow | null>(null);
  const [editing, setEditing] = useState<MobileUserRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Related children (lazy-loaded when the detail sheet opens).
  const [childGroups, setChildGroups] = useState<MobileChildGroup[]>([]);
  const [childBusy, setChildBusy] = useState(false);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);

      const rawUsers = await fetchAllRows((from, to) => apiDb
        .from('csm_user_master')
        .select(`
          user_id, email, display_name, is_admin, is_active, preferred_locale,
          manager_user_id,
          assignments:csm_user_role_assignment!fk_csm_ura_user_id (
            role_id, effective_to,
            csm_role_master:role_id ( internal_code )
          )
        `)
        .order('display_name', { ascending: true }).order('user_id').range(from, to));

      type RawUser = Omit<MobileUserRow, 'role_codes'> & {
        assignments?: { effective_to: string | null; csm_role_master?: { internal_code: string } | null }[];
      };
      const rows: MobileUserRow[] = ((rawUsers ?? []) as RawUser[]).map((u) => ({
        user_id: u.user_id,
        email: u.email,
        display_name: u.display_name,
        is_admin: u.is_admin,
        is_active: u.is_active,
        preferred_locale: u.preferred_locale,
        manager_user_id: u.manager_user_id,
        role_codes: (u.assignments ?? [])
          .filter((a) => !a.effective_to || a.effective_to >= today)
          .map((a) => a.csm_role_master?.internal_code)
          .filter((c): c is string => Boolean(c)),
      }));
      setUsers(rows);

      // Resolve role codes to translated names for display (§17.B4 — never the code).
      const { data: rawRoles, error: rErr } = await apiDb
        .from('csm_role_master')
        .select('role_id, internal_code, csm_role_master_tl ( locale_code, display_text )')
        .eq('is_active', 'Y');
      if (rErr) throw rErr;

      type RawRole = { internal_code: string; csm_role_master_tl?: TlRow[] };
      const names: Record<string, string> = {};
      for (const r of (rawRoles ?? []) as RawRole[]) {
        const tls = r.csm_role_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        names[r.internal_code] = exact?.display_text ?? fb?.display_text ?? r.internal_code;
      }
      setRoleNames(names);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (role labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  // Lazy-load the selected user's children when the detail sheet opens: the roles
  // they hold (current assignments) and the users who report to them. Names
  // resolved via _tl against the active locale, with EN_US fallback.
  useEffect(() => {
    if (!selected) { setChildGroups([]); setChildBusy(false); return; }
    const userId = selected.user_id;
    let cancelled = false;
    setChildBusy(true);
    void (async () => {
      const one = <T,>(v: T | T[] | undefined): T | undefined => (Array.isArray(v) ? v[0] : v);
      const pickTl = (tls: TlRow[] | undefined, fb: string) => {
        const list = tls ?? [];
        return list.find((x) => x.locale_code === activeLocale)?.display_text
          ?? list.find((x) => x.locale_code === 'EN_US')?.display_text
          ?? list[0]?.display_text ?? fb;
      };
      const byLabel = (a: MobileChildItem, b: MobileChildItem) => a.label.localeCompare(b.label);
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [ura, reports] = await Promise.all([
          apiDb.from('csm_user_role_assignment')
            .select('role_id, effective_to, csm_role_master ( internal_code, csm_role_master_tl ( locale_code, display_text ) )')
            .eq('user_id', userId),
          apiDb.from('csm_user_master')
            .select('user_id, display_name, email')
            .eq('manager_user_id', userId),
        ]);

        type RoleEmbed = { internal_code: string; csm_role_master_tl?: TlRow[] };
        type UraRow = { role_id: number; effective_to: string | null; csm_role_master?: RoleEmbed | RoleEmbed[] };
        const roles: MobileChildItem[] = ((ura.data ?? []) as UraRow[])
          .filter((r) => r.effective_to == null || r.effective_to >= today)
          .map((r) => { const rm = one(r.csm_role_master); return { id: r.role_id, label: pickTl(rm?.csm_role_master_tl, rm?.internal_code ?? `#${r.role_id}`), sub: rm?.internal_code }; })
          .sort(byLabel);

        type ReportRow = { user_id: number; display_name: string | null; email: string };
        const directReports: MobileChildItem[] = ((reports.data ?? []) as ReportRow[])
          .map((u) => ({ id: u.user_id, label: u.display_name ?? u.email, sub: u.email }))
          .sort(byLabel);

        if (cancelled) return;
        setChildGroups([
          { key: 'roles', table: 'csm_role_master', pkColumn: 'role_id', title: t('mobile.users.related.roles', 'Roles'), items: roles },
          { key: 'reports', table: 'csm_user_master', pkColumn: 'user_id', title: t('mobile.users.related.reports', 'Direct Reports'), items: directReports },
        ]);
      } catch {
        if (!cancelled) setChildGroups([]);
      } finally {
        if (!cancelled) setChildBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, activeLocale]);

  const roleLabel = useCallback((code: string) => roleNames[code] ?? code, [roleNames]);

  // Mimic and activate/deactivate are per-TARGET capabilities, not page verbs
  // (20260826000000_csm_delegated_deactivate_mimic). A CSM.SUPER_ADMIN may act
  // on anyone; everyone else needs the matching flag on their own record AND
  // the target beneath them in the reporting line.
  //
  // Same gate as the desktop page, over the same shared helper — the two
  // presentation layers are separate code (§16) but must not disagree about
  // who may do what, or one of them offers an action the server refuses.
  const isSuper = isSuperAdmin(session);
  const mySubtree = useMemo(
    () => collectReportingSubtree(users, session.effective_user.user_id),
    [users, session.effective_user.user_id],
  );
  const canDeactivateUser = (u: MobileUserRow) =>
    isSuper || (session.effective_user.can_deactivate_users && mySubtree.has(u.user_id));
  const canMimicUser = (u: MobileUserRow) =>
    isSuper || (session.effective_user.can_mimic_users && mySubtree.has(u.user_id));

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFacet === 'active' && u.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && u.is_active !== 'N') return false;
      if (!q) return true;
      return (
        u.email.toLowerCase().includes(q) ||
        u.display_name.toLowerCase().includes(q) ||
        u.role_codes.some((c) => roleLabel(c).toLowerCase().includes(q) || c.toLowerCase().includes(q))
      );
    });
  }, [users, filter, statusFacet, roleLabel]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.users.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const rolesText = (u: MobileUserRow) => {
    if (u.role_codes.length === 0) return t('common.none', 'None');
    const labels = u.role_codes.map(roleLabel);
    return labels.length <= 2 ? labels.join(', ') : `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
  };

  const toggleActive = async (u: MobileUserRow) => {
    setActionBusy(true);
    setError(null);
    const next = u.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'csm_user_master', 'user_id', u.user_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.users.title', 'Users')}
        description={t('mobile.users.description', 'People with access to this workspace, their roles and status.')}
      />

      <MobileStatStrip
        stats={[
          { value: users.length, label: t('mobile.users.stat.total', 'Total') },
          { value: users.filter((u) => u.is_active === 'Y').length, label: t('mobile.users.stat.active', 'Active') },
          { value: users.filter((u) => u.is_admin === 'Y').length, label: t('mobile.users.stat.admins', 'Admins') },
          { value: users.reduce((n, u) => n + u.role_codes.length, 0), label: t('mobile.users.stat.roles', 'Roles') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.users.search', 'Search users')}
          aria-label={t('mobile.users.search', 'Search users')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.users.none', 'No users match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((u) => (
            <MobileCardRow
              key={u.user_id}
              lead={initialsOf(u)}
              leadTone="blue"
              title={u.display_name || u.email}
              subtitle={`${u.email} · ${rolesText(u)}`}
              trailing={
                <MobileStatusPill tone={u.is_active === 'Y' ? 'ok' : 'no'}>
                  {u.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(u)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.users.detail', 'User detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{initialsOf(selected)}</span>
              <span>
                <div className="m-nm">{selected.display_name || selected.email}</div>
                <div className="m-em">{selected.email}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.users.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.users.type', 'Type')}
                value={selected.is_admin === 'Y' ? t('common.boolean.admin', 'Admin') : t('common.boolean.standard', 'Standard')}
              />
              <MobileDetailRow
                label={t('mobile.users.roles', 'Roles')}
                value={selected.role_codes.length === 0 ? t('common.none', 'None') : selected.role_codes.map(roleLabel).join(', ')}
              />
              {/* §17.B4: resolve the locale code (EN_US) to its display name. */}
              <MobileDetailRow
                label={t('mobile.users.locale', 'Locale')}
                value={localeName(selected.preferred_locale, activeLocale)}
              />
            </MobileContentCard>
            <MobileRelatedChildren groups={childGroups} busy={childBusy} session={session} />
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.users.edit', 'Edit')}
              </button>)}
              {canChange && canDeactivateUser(selected) && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              {onBeginMimic && canMimicUser(selected) && (
                <button
                  type="button"
                  className="m-btn m-btn-ghost"
                  disabled={actionBusy || selected.user_id === session.real_admin_id}
                  onClick={() => { const id = selected.user_id; setSelected(null); onBeginMimic(id); }}
                >
                  <Repeat size={16} />
                  {t('users.list.actions.mimic', 'Mimic')}
                </button>
              )}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileUserEditSheet
        session={session}
        canToggleActive={editing ? canDeactivateUser(editing) : false}
        user={editing ? {
          user_id: editing.user_id,
          display_name: editing.display_name,
          email: editing.email,
          preferred_locale: editing.preferred_locale,
          is_active: editing.is_active,
          role_labels: editing.role_codes.map(roleLabel),
        } : null}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// §17.B13 drill-down (MOBILE) — this page owns the mobile user sheet and
// registers it for other mobile pages' related-children blocks. Module-load
// side effect, so nothing is exported across pages and no cycle forms.
//
// The registry is mobile-specific by design (§16): entries hold COMPONENTS, and
// a shared one would let a DESKTOP modal resolve on a phone.
// ---------------------------------------------------------------------------
function MobileUserChildEditor({ session, pk, onClose, onSaved }: MobileChildEditorProps) {
  const activeLocale = useActiveLocale();
  // Opened from another page's related-children block, so there is no reporting
  // line to hand: ask the server about this one user (see use_can_deactivate_user).
  const canToggleActive = useCanDeactivateUser(pk);
  const [user, setUser] = useState<MobileEditableUser | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // MobileEditableUser needs the role LABELS, not codes, so the role
      // assignments are resolved through _tl exactly as the list does.
      const { data } = await apiDb
        .from('csm_user_master')
        .select(`
          user_id, display_name, email, preferred_locale, is_active,
          assignments:csm_user_role_assignment!fk_csm_ura_user_id (
            effective_to, csm_role_master:role_id (
              internal_code, csm_role_master_tl ( locale_code, display_text ) ) )
        `)
        .eq('user_id', pk).maybeSingle();
      if (cancelled) return;
      if (data) {
        const u = data as {
          user_id: number; display_name: string; email: string;
          preferred_locale: string; is_active: 'Y' | 'N';
          assignments?: { effective_to: string | null; csm_role_master?: {
            internal_code: string; csm_role_master_tl?: { locale_code: string; display_text: string }[] } | null }[];
        };
        const today = new Date().toISOString().slice(0, 10);
        setUser({
          user_id: u.user_id,
          display_name: u.display_name,
          email: u.email,
          preferred_locale: u.preferred_locale,
          is_active: u.is_active,
          role_labels: (u.assignments ?? [])
            .filter(a => !a.effective_to || a.effective_to >= today)
            .map(a => {
              const r = a.csm_role_master;
              if (!r) return null;
              const tls = r.csm_role_master_tl ?? [];
              return (tls.find(x => x.locale_code === activeLocale)
                   ?? tls.find(x => x.locale_code === 'EN_US') ?? tls[0])?.display_text ?? r.internal_code;
            })
            .filter((x): x is string => !!x),
        });
      }
      setBusy(false);
    })();
    return () => { cancelled = true; };
  }, [pk, activeLocale]);

  // The sheet renders nothing until it has a user, which matches how the list
  // page mounts it (it is always present, driven by a nullable `user`).
  if (busy) return null;
  return <MobileUserEditSheet session={session} user={user} canToggleActive={canToggleActive} onClose={onClose} onSaved={onSaved} />;
}

registerMobileChildEditor('csm_user_master', {
  pageCode: PAGE_CODE,
  Editor: MobileUserChildEditor,
});
